import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProjectTask, ProjectTaskCreateRequest, ProjectTaskUpdateRequest, TaskStatus, TaskFilterDto, ChecklistItem, ChecklistItemCreateRequest, ChecklistItemUpdateRequest } from '../types/api';
import { tasksNew, checklistItemService } from '../services/api';
import { useCacheConfig } from '../contexts/cache-config-context';

export const useAllTasks = () => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['tasks'],
    queryFn: () => tasksNew.getAllTasks(),
    staleTime: getCacheTime('tasks'),
  });
};

export const useTask = (id: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['tasks', id],
    queryFn: () => tasksNew.getTaskById(id),
    staleTime: getCacheTime('tasks'),
    enabled: !!id,
  });
};

export const useTasksByRequest = (requestId: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['tasks', 'request', requestId],
    queryFn: () => tasksNew.getTasksByRequest(requestId),
    staleTime: getCacheTime('tasks'),
    enabled: !!requestId,
  });
};

export const useFilteredTasks = (filters: TaskFilterDto) => {
  const { getCacheTime } = useCacheConfig();
  
  // Extendiamo il filtro con le proprietà di SearchParams
  const searchParams = { 
    ...filters,
    pageNumber: filters.pageNumber || 1,
    pageSize: filters.pageSize || 10
  };
  
  return useQuery({
    queryKey: ['tasks', 'filtered', filters],
    queryFn: () => tasksNew.getAllTasks(searchParams),
    staleTime: getCacheTime('tasks'),
  });
};

export const useTasksByStatus = (status: TaskStatus) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['tasks', 'status', status],
    queryFn: () => tasksNew.getTasksByPriority(status as any), // Temporary fix - needs backend alignment
    staleTime: getCacheTime('tasks'),
  });
};

export const useTasksByUser = (userId: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['tasks', 'user', userId],
    queryFn: () => tasksNew.getTasksAssignedToUser(userId),
    staleTime: getCacheTime('tasks'),
    enabled: !!userId,
  });
};

export const useCreateTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (newTask: ProjectTaskCreateRequest) => tasksNew.createTask(newTask),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, task }: { id: string; task: ProjectTaskUpdateRequest }) =>
      tasksNew.updateTask(id, task),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.id] });
    },
  });
};

export const useUpdateTaskStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: TaskStatus; notes?: string }) =>
      tasksNew.updateTaskStatus(id, status, notes),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.id] });
    },
  });
};

export const useAssignTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ taskId, userId }: { taskId: string; userId: string }) =>
      tasksNew.assignTask(taskId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useUnassignTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ taskId }: { taskId: string }) =>
      tasksNew.unassignTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// ================== CHECKLIST HOOKS ==================
export const useTaskChecklist = (taskId: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['checklist', 'task', taskId],
    queryFn: () => checklistItemService.getChecklistItemsByTask(taskId),
    staleTime: getCacheTime('tasks'),
    enabled: !!taskId,
  });
};

export const useAddChecklistItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ taskId, item }: { taskId: string; item: ChecklistItemCreateRequest }) =>
      checklistItemService.addChecklistItemToTask(taskId, item),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['checklist', 'task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useUpdateChecklistItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, item, taskId }: { id: string; item: ChecklistItemUpdateRequest; taskId: string }) =>
      checklistItemService.updateChecklistItem(id, item),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['checklist', 'task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useDeleteChecklistItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, taskId }: { id: string; taskId: string }) =>
      checklistItemService.deleteChecklistItem(id),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['checklist', 'task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useToggleChecklistItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, taskId }: { id: string; taskId: string }) =>
      checklistItemService.toggleChecklistItemCompletion(id),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['checklist', 'task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => tasksNew.deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useDuplicateTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => tasksNew.duplicateTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useArchiveTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => 
      // Usar el status correcto del enum
      tasksNew.updateTaskStatus(id, TaskStatus.Done), // Done as archived
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useUnarchiveTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => 
      // Usar el status correcto del enum
      tasksNew.updateTaskStatus(id, TaskStatus.ToDo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// Hooks aggiuntivi per le funzionalità specifiche del TaskService
export const useKanbanTasks = (requestId?: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['tasks', 'kanban', requestId],
    queryFn: async () => {
      if (requestId) {
        return await tasksNew.getTasksByRequest(requestId);
      }
      const result = await tasksNew.getAllTasks();
      return result.items || [];
    },
    staleTime: getCacheTime('tasks'),
  });
};

export const useUpdateTaskPosition = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, position, status }: { id: string; position: number; status?: TaskStatus }) =>
      tasksNew.moveTaskToColumn(id, position),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useTaskStats = (id: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['tasks', id, 'stats'],
    queryFn: () => tasksNew.getTaskStatistics(),
    staleTime: getCacheTime('tasks'),
    enabled: !!id,
  });
};

// Rimuoviamo useTaskTimeline poiché non esiste nel nuovo servizio
// export const useTaskTimeline = (id: string) => { ... }

// Rimuoviamo useBulkUpdateTasks poiché non esiste nel nuovo servizio
// export const useBulkUpdateTasks = () => { ... }

// Rimuoviamo useBulkDeleteTasks poiché non esiste nel nuovo servizio
// export const useBulkDeleteTasks = () => { ... }

export const useSearchTasks = (query: string, filters?: any, limit?: number) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['tasks', 'search', query, filters, limit],
    queryFn: () => tasksNew.searchTasks({ searchTerm: query, ...filters }),
    staleTime: getCacheTime('tasks'),
    enabled: !!query,
  });
};

// Rimuoviamo useTaskTags poiché non esiste nel nuovo servizio
// export const useTaskTags = () => { ... }

// Rimuoviamo useAddTaskTag poiché non esiste nel nuovo servizio
// export const useAddTaskTag = () => { ... }

// Rimuoviamo useRemoveTaskTag poiché non esiste nel nuovo servizio
// export const useRemoveTaskTag = () => { ... }

// Task lifecycle hooks
// Rimuoviamo useStartTask poiché non esiste nel nuovo servizio
// export const useStartTask = () => { ... }

// Rimuoviamo useStopTask poiché non esiste nel nuovo servizio
// export const useStopTask = () => { ... }

export const useCompleteTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => tasksNew.completeTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useReopenTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => tasksNew.reopenTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// Rimuoviamo i time tracking hooks poiché non esistono nel nuovo servizio
// export const useLogTaskTime = () => { ... }
// export const useTaskTimeEntries = (id: string) => { ... }

// Composite hook for backward compatibility
export const useTasks = () => {
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const deleteTaskMutation = useDeleteTask();
  const updateStatusMutation = useUpdateTaskStatus();
  const assignMutation = useAssignTask();
  const unassignMutation = useUnassignTask();
  
  return {
    createTaskMutation,
    updateTaskMutation,
    deleteTaskMutation,
    updateStatusMutation,
    assignMutation,
    unassignMutation,
    
    // Queries - these need to be called with parameters
    useAllTasks,
    useTask,
    useTasksByRequest,
    useFilteredTasks,
    useTasksByStatus,
    useTasksByUser,
    useKanbanTasks,
    useTaskStats,
    // useTaskTimeline, // Removed - not available in new service
    
    // Checklist hooks
    useTaskChecklist,
    useAddChecklistItem,
    useUpdateChecklistItem,
    useDeleteChecklistItem,
    useToggleChecklistItem,
    
    // Additional mutations
    useDuplicateTask,
    useArchiveTask,
    useUnarchiveTask,
    useUpdateTaskPosition,
    useCompleteTask,
    useReopenTask,
    useSearchTasks,
    // useBulkUpdateTasks, // Removed - not available in new service
    // useLogTaskTime, // Removed - not available in new service
    // useTaskTimeEntries, // Removed - not available in new service
  };
};